import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Send, ArrowLeft } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { testimonialsAPI, getErrorMessage } from '../../lib/api';

const WriteReview = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    rating: 5,
    content: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error({
        title: 'Validation Error',
        description: 'Please enter a testimonial title'
      });
      return;
    }

    if (formData.title.trim().length < 5) {
      toast.error({
        title: 'Validation Error',
        description: 'Title must be at least 5 characters long'
      });
      return;
    }

    if (!formData.content.trim()) {
      toast.error({
        title: 'Validation Error',
        description: 'Please share your review'
      });
      return;
    }

    if (formData.content.trim().length < 20) {
      toast.error({
        title: 'Validation Error',
        description: 'Review must be at least 20 characters long'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await testimonialsAPI.writeTestimonial({
        title: formData.title.trim(),
        content: formData.content.trim(),
        rating: parseInt(formData.rating)
      });
      
      toast.success({
        title: 'Review Submitted!',
        description: response.data?.message || 'Your testimonial has been submitted and is pending admin approval.'
      });
      
      // Reset form
      setFormData({
        title: '',
        rating: 5,
        content: ''
      });
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error({
        title: 'Submission Failed',
        description: getErrorMessage(error)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-lg p-8 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/20 rounded-lg">
            <Star size={32} fill="currentColor" />
          </div>
          <div>
            <h1 className="text-3xl font-bold mb-1">Share Your Experience</h1>
            <p className="text-blue-100">We value your feedback and would love to hear about your experience with Climb Capital</p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Testimonial Title */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Testimonial Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Give your testimonial a brief title"
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
              required
            />
            <p className="text-sm text-gray-500 mt-1">A brief, catchy title for your testimonial</p>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Rating <span className="text-red-500">*</span>
            </label>
            <select
              name="rating"
              value={formData.rating}
              onChange={handleChange}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
              required
            >
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">Rate your overall experience (1-5 stars)</p>

            {/* Star Display */}
            <div className="flex items-center gap-1 mt-3">
              {[...Array(5)].map((_, index) => (
                <Star
                  key={index}
                  size={24}
                  className={index < formData.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                />
              ))}
              <span className="ml-2 text-gray-600 font-semibold">{formData.rating} out of 5</span>
            </div>
          </div>

          {/* Review Text */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Your Review <span className="text-red-500">*</span>
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows="8"
              placeholder="Share your experience with Climb Capital..."
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors resize-none"
              required
            />
            <p className="text-sm text-gray-500 mt-1">Please share your detailed experience with our services (minimum 20 characters)</p>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              <ArrowLeft size={20} />
              Back to Dashboard
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <Send size={20} />
              {loading ? 'Submitting...' : 'Submit Testimonial'}
            </button>
          </div>
        </form>
      </div>

      {/* Info Card */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
        <h3 className="font-bold text-blue-900 mb-2">Why Your Feedback Matters</h3>
        <p className="text-sm text-blue-800">
          Your testimonial helps us improve our services and assists other investors in making informed decisions. 
          All reviews are verified and may be featured on our website and marketing materials.
        </p>
      </div>
    </div>
  );
};

export default WriteReview;
